import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tab from '@mui/material/Tab';
import TabsBase from '@mui/material/Tabs';
import withCx, { CxProps } from 'fe-core/util/withCx';
import Box from 'fe-design-base/atoms/Box';
import Icon from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';
import Badge, { BadgeVariant } from 'fe-design-base/molecules/Badge';
import { partial } from 'lodash';

import { EVENT_ACTIONS, TRACK_ACTION_TYPES } from 'util/tracking_constants';
import { useTrackUx } from 'util/uxEvents';

export interface TabOptionProps {
  value: string | number;
  label: string | JSX.Element;
  iconName?: 'Warning' | 'Critical';
  badgeText?: number | string;
  badgeVariant?: BadgeVariant;
  disabled?: boolean;
}
export type OnChangeType = (
  event: React.SyntheticEvent,
  newValue: number | string
) => void;
export interface TabsProps {
  options: TabOptionProps[];
  disabled?: boolean;
  onChange?: OnChangeType;
  tabValue?: number | string;
  scrollable?: boolean;
  ariaName?: string;
  uxElement?: string;
  dataTestId?: string;
  noPadding?: boolean;
}

const Tabs = ({
  ariaName,
  cx,
  cxEl,
  dataTestId = 'FDBTab',
  disabled,
  onChange,
  options,
  scrollable,
  tabValue,
  uxElement,
  noPadding,
}: TabsProps & CxProps) => {
  const tabRefs = useRef<HTMLButtonElement[]>([]);
  const [activeTab, setActiveTab] = React.useState<number | string>(
    tabValue || options[0].value
  );
  const [aTabHasFocus, setATabHasFocus] = useState(false);
  const [focusedTab, setFocusedTab] = useState<number | string>();

  const trackUx = useTrackUx({
    element: uxElement,
  } as any);

  useEffect(() => {
    if (tabValue !== undefined) setActiveTab(tabValue);
    if (!aTabHasFocus) setFocusedTab(undefined);
  }, [tabValue, aTabHasFocus]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: number | string) => {
      const selectedTab = options.find(option => option.value === newValue);

      if (disabled || selectedTab?.disabled) {
        event.preventDefault();
        return;
      }

      if (uxElement) {
        trackUx(EVENT_ACTIONS.TAB_CLICKED, TRACK_ACTION_TYPES.CLICK, {
          value: newValue,
          tabText: selectedTab?.label,
          icon: selectedTab?.iconName,
        });
      }

      setActiveTab(newValue);
      onChange?.(event, newValue);
    },
    [disabled, onChange, options, trackUx, uxElement]
  );

  const onSetTabRef = (element: any) => tabRefs.current.push(element);

  const onFocusVisible = (value: number | string) => {
    setFocusedTab(value);
    setATabHasFocus(true);
  };

  const onBlurCapture = () => {
    /** Slowing this down to prevent race condition with onFocusVisible */
    setTimeout(() => {
      setATabHasFocus(
        tabRefs.current.some(tabRef => tabRef === document.activeElement)
      );
    }, 100);
  };

  return (
    <Box pv={noPadding ? 0 : 20}>
      <TabsBase
        className={cx({ hideActiveLine: activeTab === focusedTab })}
        value={activeTab}
        onChange={handleChange}
        aria-label={`${ariaName}-tabs`}
        data-testid={dataTestId}
        variant={scrollable ? 'scrollable' : 'fullWidth'}
        scrollButtons={false}
      >
        {options.map(
          ({
            label,
            value,
            disabled: optionDisabled,
            iconName,
            badgeText,
            badgeVariant = 'loud',
          }) => {
            const selectedTab = activeTab === value;
            return (
              <Tab
                key={`${label}-${value}`}
                value={value}
                ref={onSetTabRef}
                tabIndex={0}
                data-testid={`${dataTestId}-${value}`}
                aria-disabled={disabled || optionDisabled}
                className={cxEl('tab', { iconName, scrollable })}
                onFocusVisible={partial(onFocusVisible, value)}
                onBlurCapture={onBlurCapture}
                label={
                  <Box row>
                    <Box mr={iconName || badgeText ? 8 : 0}>
                      <Text component="div" variant="body">
                        {label}
                      </Text>
                    </Box>
                    {iconName && !badgeText && (
                      <Box vcenter ml={selectedTab ? -4 : ''}>
                        <Icon
                          iconName={iconName}
                          size="small"
                          color={
                            iconName === 'Critical' ? 'error300' : 'warning300'
                          }
                        />
                      </Box>
                    )}
                    {badgeText && (
                      <Box vcenter ml={selectedTab ? -4 : ''}>
                        <Badge
                          text={badgeText}
                          variant={badgeVariant}
                          disabled={disabled || optionDisabled}
                        />
                      </Box>
                    )}
                  </Box>
                }
              />
            );
          }
        )}
      </TabsBase>
    </Box>
  );
};

export default withCx<TabsProps>('FDBTabs')(Tabs);
